import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, MenuItem } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import academicSessionService from '@/services/academicSessionService';
import { useSchool } from '@/hooks/useSchool';
import toast from 'react-hot-toast';

const validationSchema = Yup.object().shape({
  session_name: Yup.string()
    .trim()
    .required('Session name is required')
    .matches(/^[a-zA-Z0-9\s-_/]+$/, 'Session name must not contain special symbols'),
  start_date: Yup.date().required('Start date is required'),
  end_date: Yup.date()
    .min(Yup.ref('start_date'), 'End date cannot be before start date')
    .required('End date is required'),
  status: Yup.string().oneOf(['ACTIVE', 'INACTIVE']).required('Status is required'),
});

export function AcademicSessionForm({ open, onClose, onSuccess, session }) {
  const { schoolId } = useSchool();
  const isEdit = Boolean(session);

  const formik = useFormik({
    initialValues: {
      session_name: session?.session_name || '',
      start_date: session?.start_date ? session.start_date.split('T')[0] : '',
      end_date: session?.end_date ? session.end_date.split('T')[0] : '',
      status: session?.status || 'ACTIVE',
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const payload = { ...values };
        if (!isEdit) {
          payload.school_id = schoolId;
          payload.is_current = false; // default false
          await academicSessionService.createSession(payload);
          toast.success('Session created successfully');
        } else {
          await academicSessionService.updateSession(session.id, payload);
          toast.success('Session updated successfully');
        }
        onSuccess();
      } catch (error) {
        toast.error(isEdit ? 'Failed to update session' : 'Failed to create session');
        console.error(error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Session' : 'Add New Session'}</DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              name="session_name"
              label="Session Name (e.g. 2024-2025)"
              fullWidth
              value={formik.values.session_name}
              onChange={formik.handleChange}
              error={formik.touched.session_name && Boolean(formik.errors.session_name)}
              helperText={formik.touched.session_name && formik.errors.session_name}
            />

            <TextField
              name="start_date"
              label="Start Date"
              type="date"
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
              value={formik.values.start_date}
              onChange={formik.handleChange}
              error={formik.touched.start_date && Boolean(formik.errors.start_date)}
              helperText={formik.touched.start_date && formik.errors.start_date}
            />

            <TextField
              name="end_date"
              label="End Date"
              type="date"
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
              value={formik.values.end_date}
              onChange={formik.handleChange}
              error={formik.touched.end_date && Boolean(formik.errors.end_date)}
              helperText={formik.touched.end_date && formik.errors.end_date}
            />

            <TextField
              select
              name="status"
              label="Status"
              fullWidth
              value={formik.values.status}
              onChange={formik.handleChange}
              error={formik.touched.status && Boolean(formik.errors.status)}
              helperText={formik.touched.status && formik.errors.status}
            >
              <MenuItem value="ACTIVE">ACTIVE</MenuItem>
              <MenuItem value="INACTIVE">INACTIVE</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} color="inherit">
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={formik.isSubmitting}
          >
            {isEdit ? 'Save Changes' : 'Create Session'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
